/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'wouter';

import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { Button, FontFaces, Spinner } from '@micromag/core/components';
import { useUrlGenerator } from '@micromag/core/contexts';
import { useParsedStory } from '@micromag/core/hooks';
import { Screens } from '@micromag/editor';
import { ScreensProvider } from '@micromag/screens';

import { useOrganisation } from '../../../contexts/OrganisationContext';
import useStory from '../../../hooks/useStory';
import useStoryVersion from '../../../hooks/useStoryVersion';

import { isTouchDevice } from '../../../lib/utils';
import SettingsButton from '../../buttons/Settings';
import MainLayout from '../../layouts/Main';
import StoryMenubar from '../../menubars/Story';
import StoryVersionMenubar from '../../menubars/StoryVersion';
import ScreenMenu from '../../menus/Screen';
import Page from '../../partials/Page';

import styles from '../../../../styles/pages/stories/show.module.scss';

const propTypes = {
    location: MicromagPropTypes.location.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const ShowPage = ({ className }) => {
    const [pathname] = useLocation();
    const { story: storyId = null, version: versionId = null } = useParams();
    const url = useUrlGenerator();
    const { story: loadedStory, loading } = useStory(storyId, { withoutCache: true });
    const [story, setStory] = useState(loadedStory);
    const hasStory = story !== null;
    const { last_version: lastVersion = null } = story || {};
    const { id: lastVersionId = null } = lastVersion || {};

    const organisation = useOrganisation();
    const { permissions: organisationPermissions = null } = organisation || {};
    const { can_edit_story: organisationCanEditStory = false } = organisationPermissions || {};
    const canEditStory = organisation === null || organisationCanEditStory;

    const version = useStoryVersion(storyId, versionId, {
        autoload: storyId && versionId !== null,
    });
    const isCurrentVersion = versionId === lastVersionId;

    const hasVersion = version !== null;
    const isVersionView = versionId !== null;
    const { components: parsedScreens = null, fonts = null } =
        useParsedStory(isVersionView && hasVersion ? version : story) || {};

    const screens = (parsedScreens || []).reduce((acc, screen) => {
        const { id = null, parentId = null } = screen || {};
        if (parentId === null) {
            acc.push(screen);
            const children = (parsedScreens || []).reduce((its, child) => {
                if (child.parentId !== null && child.parentId === id) {
                    its.push(child);
                }
                return its;
            }, []);
            return acc.concat(children);
        }
        return acc;
    }, []);

    const finalFonts = useMemo(() => fonts || [], [fonts]);
    const hasScreens = screens !== null && screens.length > 0;

    const versionsTitle = (
        <FormattedMessage defaultMessage="Versions" description="Versions title" />
    );
    const versionTitle = (
        <FormattedMessage defaultMessage="Consult a version" descrition="Consult a version title" />
    );

    const storyTitle = hasStory ? (
        story.title
    ) : (
        <FormattedMessage defaultMessage="Micromag" description="Page title" />
    );
    const mainNavLabel = isVersionView ? versionTitle : storyTitle;

    const createFirstScreen = (
        <div className="container-fluid text-center bg-dark text-light p-5">
            <h1 className="display-4">
                <FormattedMessage
                    defaultMessage="No screen yet"
                    description="No screen yet status"
                />
            </h1>
            {canEditStory ? (
                <>
                    <p className="lead mt-4 mb-4">
                        <FormattedMessage
                            defaultMessage="It’s time to start creating!"
                            description="Time to create status"
                        />
                    </p>
                    <p className="lead pt-4">
                        {hasStory ? (
                            <Button
                                href={`${url('stories.editor', {
                                    story: story.id,
                                })}?create=1`}
                                theme="primary"
                                size="lg"
                            >
                                <FormattedMessage
                                    defaultMessage="Create your first screen"
                                    description="Create first screen button label"
                                />
                            </Button>
                        ) : null}
                    </p>
                </>
            ) : null}
        </div>
    );

    const noScreensContent = isVersionView ? (
        <FormattedMessage
            defaultMessage="No screens in this version"
            descrition="No screens in this version message"
        />
    ) : (
        createFirstScreen
    );

    const preview = loading ? <Spinner /> : noScreensContent;

    const currentNavItem = { label: mainNavLabel, url: pathname };
    const versionParentNavItems = hasStory
        ? [
              {
                  label: storyTitle,
                  url: url('stories.show', { story: storyId }),
              },
              {
                  label: versionsTitle,
                  url: url('stories.versions.index', { story: storyId }),
              },
          ]
        : [];
    const navItems = isVersionView ? [...versionParentNavItems, currentNavItem] : [currentNavItem];

    const touchDevice = isTouchDevice();

    const onScreenMenuStoryChange = useCallback(
        (newStory) => {
            setStory(newStory);
        },
        [setStory],
    );

    useEffect(() => {
        setStory(loadedStory);
    }, [loadedStory, setStory]);

    // console.log('yo', screens);

    return (
        <MainLayout nav={navItems}>
            <Page
                title={!isVersionView ? storyTitle : null}
                menubar={
                    // eslint-disable-next-line no-nested-ternary
                    hasStory ? (
                        // eslint-disable-next-line no-nested-ternary
                        isVersionView ? (
                            hasVersion ? (
                                <StoryVersionMenubar
                                    story={story}
                                    version={version}
                                    current={isCurrentVersion}
                                />
                            ) : null
                        ) : (
                            <StoryMenubar story={story} />
                        )
                    ) : null
                }
                className={classNames([
                    styles.container,
                    {
                        [className]: className !== null,
                        [styles.isMouseDevice]: !touchDevice,
                    },
                ])}
            >
                <FontFaces fonts={finalFonts} />
                <ScreensProvider>
                    {hasStory && hasScreens ? (
                        <Screens
                            items={screens.map((it) => {
                                const versionViewPath = isVersionView
                                    ? url('stories.versions.preview.path', {
                                          story: storyId,
                                          version: versionId,
                                          path: it.id,
                                      })
                                    : null;
                                const normalViewPath = canEditStory
                                    ? url('stories.editor.path', {
                                          story: storyId,
                                          path: it.id,
                                      })
                                    : url('stories.preview.path', {
                                          story: storyId,
                                          path: it.id,
                                      });
                                console.log(
                                    'screen href',
                                    isVersionView ? versionViewPath : normalViewPath,
                                );
                                return {
                                    id: it.id,
                                    screen: it,
                                    href: isVersionView ? versionViewPath : normalViewPath,
                                };
                            })}
                            withPreview
                            isTree={false}
                            itemClassName={styles.item}
                            buttonClassName={styles.itemButton}
                            settingsClassName={styles.itemSettings}
                            settings={
                                !isVersionView && canEditStory
                                    ? // eslint-disable-next-line react/no-unstable-nested-components
                                      (index) => (
                                          // eslint-disable-next-line react/jsx-indent
                                          <div className="p-2">
                                              <SettingsButton
                                                  theme="light"
                                                  iconOnly
                                                  size="sm"
                                                  buttonClassName="py-1 px-2"
                                                  dropdown={
                                                      <ScreenMenu
                                                          story={story}
                                                          screen={screens[index]}
                                                          onStoryChange={onScreenMenuStoryChange}
                                                      />
                                                  }
                                              />
                                          </div>
                                      )
                                    : null
                            }
                        />
                    ) : (
                        preview
                    )}
                </ScreensProvider>
            </Page>
        </MainLayout>
    );
};

ShowPage.propTypes = propTypes;
ShowPage.defaultProps = defaultProps;

export default ShowPage;
